/**
 * The artwork generator — `screens.md` §6, and the screen where every refusal c91 wrote
 * becomes a sentence somebody reads.
 *
 * ## The screen composes no refusal copy. None. Anywhere.
 *
 * U17's rule, and the whole reason `GateRefusalBody` is a distinct type on the wire: a gate
 * refusal carries `detail` (why it happened) and `remedy.label` (what to do about it), both
 * authored once in `apps/api/src/ops/artwork.ts`. This file renders those two strings and
 * decides only the **shape** the remedy gets — a button, a link, a value, or nothing —
 * routed on `remedy.action` by `artwork.ts`'s `REMEDY_SHAPES`.
 *
 * The alternative is a `switch` on `code` with copy beside it, which reads as tidy and is
 * two sources of truth for one refusal. The public `/problems/<slug>` page would be a third.
 * There is a cheap way to tell whether a screen has fallen into it: change `remedy.label` in
 * a fixture and see whether the rendered text moves. Here it does, and that is the assertion
 * the brief asks Sonnet for.
 *
 * ## Every interesting state is an exported component or a pure function
 *
 * `apps/app`'s suite is `renderToStaticMarkup` — no events, no effects — so a state
 * reachable only behind an `onClick` is unreachable by it. Two shipped defects came from
 * that gap. So each of §6's states below is exported and rendered from a **preflight row**
 * rather than from hand-built props: props prove the template, a fixture proves the wiring.
 * What genuinely needs an event is enumerated in `artwork.ts`'s `C93_FLOWS`.
 *
 * ## Two servers of truth, and the split is the API's own
 *
 * `artwork.preflight` (JSON) says **what may be said**; `POST /v1/artwork` (bytes) says
 * **what arrived**. That is not a redundancy — the file response is a file, so its advisories
 * are header codes with no prose, and the prose lives on the preflight. `api/artwork.ts`
 * carries the same paragraph from the network end.
 */
import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { Button, Input, PlanGate, Surface, Timeline } from "@packagepage/ui";
import {
  ARTWORK_BATCH_REPORT_NAME,
  BATCH_ZIP_TIER,
  batchZipName,
  planRank,
  type ArtworkBatchStatus,
  type ArtworkBatchStatusView,
  type ArtworkPreflightRow,
  type ArtworkWatermark,
  type Tier,
} from "@packagepage/shared";

import { problemOf } from "../api/client.js";
import {
  readBatchZip,
  requestArtwork,
  requestPlacementSheet,
  startBatchZip,
  type ArtworkOutcome,
} from "../api/artwork.js";
import { api } from "../api/procedures.js";
import { displayName } from "./product-row.js";
import { PREFIX_SCREEN_PATH } from "./prefix-screen.js";
import {
  BLOCKED_DOMAIN_EXITS,
  EMPTY_SETTINGS,
  FORMAT_CHOICES,
  FORMAT_LABELS,
  PLACEMENT_SHEET_LABEL,
  PRINT_CHALLENGE,
  RECHECK_INTERVAL_MS,
  SYMBOLOGY_CHOICES,
  SYMBOLOGY_LABELS,
  checkPrintChallenge,
  dnsTimelineSteps,
  downloadConfirmation,
  eccChoices,
  encodedUri,
  placementSheetRequestFor,
  preflightRequestFor,
  preflightState,
  remedyShapeOf,
  requiresPrintConfirmation,
  watermarkNotices,
  xDimensionFix,
  xDimensionOf,
  type ArtworkSettings,
  type DownloadConfirmation,
} from "./artwork.js";
import { saveBlob } from "./save-blob.js";

import "@packagepage/ui/input.css";
import "@packagepage/ui/button.css";
import "@packagepage/ui/timeline.css";
import "@packagepage/ui/gate.css";
import "./artwork.css";

export function ArtworkGenerator({ productId }: { productId: string }): ReactNode {
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState<ArtworkSettings>(EMPTY_SETTINGS);
  const [confirming, setConfirming] = useState<"print" | "escape" | null>(null);
  const [receipt, setReceipt] = useState<DownloadConfirmation | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const product = useQuery({
    queryKey: ["products", "read", productId],
    queryFn: () => api.product(productId),
  });
  const batches = useQuery({
    queryKey: ["batches", productId],
    queryFn: () => api.batches(productId),
  });
  const org = useQuery({
    queryKey: ["orgs", "current"],
    queryFn: () => api.currentOrg(),
  });

  const zip = useBatchZip(settings);

  const request = preflightRequestFor(productId, settings);

  /**
   * The gates, re-asked whenever the settings move.
   *
   * `refetchInterval` is the mock's *"we'll keep checking every 30 seconds and unlock this
   * download automatically"* — and it runs **only while a domain block is showing**, which
   * is what keeps it from being a background poll of every customer's DNS. §8.1's middle
   * rung makes a live HTTP probe per row; a screen that re-probed on a timer in every state
   * would be a denial of service with a slider on it.
   */
  const preflight = useQuery({
    queryKey: ["artwork", "preflight", request],
    queryFn: () => api.artworkPreflight(request as NonNullable<typeof request>),
    enabled: request !== null,
    refetchInterval: (query) => {
      const row = query.state.data?.results[0];
      return row !== undefined &&
        !row.eligible &&
        row.refused?.code === "DOMAIN_NEVER_LIVE"
        ? RECHECK_INTERVAL_MS
        : false;
    },
  });

  const state = preflightState({
    request,
    loading: preflight.isPending,
    row: preflight.data?.results[0],
    errorDetail:
      preflight.error === null
        ? null
        : (problemOf(preflight.error)?.detail ??
          "The preflight could not be run. Nothing was generated."),
  });

  /**
   * §6's *"draft product (block with inline publish — a button, not a wall)"*.
   *
   * **The same op the product screen's primary publish button calls** — `api.publishProduct`,
   * `products.publish`, one procedure — and that is a correctness property rather than tidy
   * reuse. §12(2)'s trust gate lives inside `publishProduct`: a GTIN under an unverified
   * prefix is refused `PREFIX_NOT_VERIFIED` at 409, and it was unenforced on this estate for
   * two months precisely because *"every artifact describing it was correct and none of them
   * was a caller"*. An inline publish that composed its own path would be a second door
   * beside that gate, and it would look exactly like this one.
   *
   * So the assertion the brief asks for is a real one and it is made at the seam: publish a
   * draft whose prefix is unverified through this route and the 409 arrives, because there
   * is only one route.
   */
  const publish = useMutation({
    mutationFn: () => api.publishProduct(productId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["products"] });
      void queryClient.invalidateQueries({ queryKey: ["artwork", "preflight"] });
    },
  });

  /**
   * The preview, and **it is the real file rather than a drawing of one.**
   *
   * Always `format: "png"`, whatever the person picked to download, and the choice is
   * §8.2's rather than a convenience: the PNG is watermarked at every tier and is never a
   * print deliverable, so a preview built from it cannot be mistaken for artwork and is not
   * withheld by the prefix gate — §8.1 row 2 blocks the three vectors and leaves this one
   * standing, deliberately, so that an unverified brand can still see its own code.
   *
   * It costs a `media` row, and that is stated rather than hidden. `artworkIdentity` is a
   * content hash, so previewing the same settings twice adopts one row and the audit event
   * fires only on a genuinely new one — a distinct row per distinct setting is a distinct
   * artwork, which is what §8's re-render promise means. `staleTime: Infinity` is what stops
   * a re-render asking again for bytes that cannot have changed.
   */
  const preview = useQuery({
    queryKey: [
      "artwork",
      "preview",
      productId,
      settings.symbology,
      settings.eccLevel,
      settings.lot,
      xDimensionOf(settings),
    ],
    queryFn: () =>
      requestArtwork({
        productId,
        symbology: settings.symbology,
        format: "png",
        xDimensionMm: xDimensionOf(settings) ?? 0,
        applicationPreset: "retail-pos",
        eccLevel: settings.eccLevel,
        escapeHatch: false,
        ...(settings.lot === null ? {} : { qualifiers: { lot: settings.lot } }),
      }),
    enabled: state.kind === "ready",
    staleTime: Infinity,
  });
  // Narrowed by a guard rather than by `?.ok === true`: optional chaining on a union does
  // not discriminate it, which is the kind of "looks narrowed" that compiles in a codebase
  // without `strict` and does not here.
  const previewFile = preview.data !== undefined && preview.data.ok ? preview.data : null;
  const previewUrl = usePreviewObjectUrl(previewFile?.blob ?? null);

  /**
   * The download, and — when the box is ticked — the sheet beside it (c96).
   *
   * ## Two requests, sequential, artwork first, and each of those is a decision
   *
   * **Two** because the sheet is a sibling endpoint rather than a fifth format: bundling the
   * bytes would have made the artwork response multipart, and `packages/shared`'s
   * `placementSheetRequestSchema` carries the rest of that argument.
   *
   * **Artwork first, and its failure ends the click.** The gate walk behind the two is the
   * same walk with one exemption, so the only way the artwork is refused and the sheet is not
   * is §8.1 row 2 — and a person who has just been told *"print files are refused for a GTIN
   * whose company prefix we have not verified"* should not simultaneously receive a file.
   * They can tick the box on its own once they have read the refusal, which is the sequence
   * where the sheet arriving means something.
   *
   * **A sheet that fails does not undo the artwork**, and it is deliberately not reported as
   * a failure of the download either. The file the person asked for is in their downloads
   * folder; the honest thing to say is that the artwork came and the sheet did not, which is
   * what leaving `sheetFilename` null and setting `refusal` says. Reporting the whole click
   * as refused would be false about the file they are holding.
   *
   * The sheet is generated with `escapeHatch: false` regardless of the artwork's, and that is
   * `placementSheetRequestFor`'s doing rather than an oversight here: §8.3's disclosure is
   * read and accepted for a *print run*, and the escape hatch only ever changes which host
   * the URI names. A blocked customer who has accepted it for their artwork gets the sheet
   * for the URI the artwork will actually carry — see below.
   */
  const download = useMutation({
    mutationFn: async (
      escapeHatch: boolean,
    ): Promise<{ artwork: ArtworkOutcome; sheetFilename: string | null }> => {
      const artwork = await requestArtwork({
        productId,
        symbology: settings.symbology,
        format: settings.format,
        xDimensionMm: xDimensionOf(settings) ?? 0,
        applicationPreset: "retail-pos",
        eccLevel: settings.eccLevel,
        escapeHatch,
        ...(settings.lot === null ? {} : { qualifiers: { lot: settings.lot } }),
      });
      if (!artwork.ok) return { artwork, sheetFilename: null };

      const sheetRequest = placementSheetRequestFor(productId, settings);
      if (sheetRequest === null) return { artwork, sheetFilename: null };

      // `escapeHatch` is carried over from the artwork request rather than taken from
      // `placementSheetRequestFor`'s conservative `false`. The sheet PRINTS the encoded
      // Digital Link URI, so a sheet built against the tenant's domain beside artwork built
      // against the platform host would put two different addresses in one downloads folder,
      // and only one of them is going on the pack.
      const sheet = await requestPlacementSheet({ ...sheetRequest, escapeHatch });
      if (!sheet.ok) return { artwork, sheetFilename: null };

      saveBlob(sheet.blob, sheet.filename);
      return { artwork, sheetFilename: sheet.filename };
    },
    onSuccess: ({ artwork: outcome, sheetFilename }) => {
      setConfirming(null);
      if (!outcome.ok) {
        setRefusal(
          outcome.problem?.detail ??
            "The file could not be generated, and nothing was produced.",
        );
        return;
      }
      setRefusal(
        settings.placementSheet && sheetFilename === null
          ? "Your artwork downloaded. The placement sheet did not — try ticking it again, " +
              "and nothing about the file you have is affected."
          : null,
      );
      setReceipt(
        downloadConfirmation({
          gtin14: product.data?.gtin14 ?? "",
          symbology: settings.symbology,
          format: settings.format,
          xDimensionMm: xDimensionOf(settings) ?? 0,
          widthMm: outcome.geometry?.widthMm ?? null,
          heightMm: outcome.geometry?.heightMm ?? null,
          filename: outcome.filename,
          watermarks: outcome.watermarks,
          sheetFilename,
        }),
      );
      saveBlob(outcome.blob, outcome.filename);
      // A render is a `media` row, so the product's own media list is now stale. The sheet is
      // one too — same table, `meta.format: "placement"` — so one invalidation covers both.
      void queryClient.invalidateQueries({ queryKey: ["media", productId] });
    },
  });

  if (product.isPending) {
    return (
      <Surface>
        <p data-screen-label="Artwork generator">Loading this product&hellip;</p>
      </Surface>
    );
  }

  if (product.error !== null) {
    return (
      <Surface tone="error" emphasis="filled">
        <div data-screen-label="Artwork generator">
          <strong>
            {problemOf(product.error)?.title ?? "We could not load this product"}
          </strong>
          <p>{problemOf(product.error)?.detail ?? "Nothing was generated."}</p>
          <p>
            <Link to="/products">Back to the catalogue</Link>
          </p>
        </div>
      </Surface>
    );
  }

  const row = product.data;
  const name = displayName(row.name, row.gtin14);
  const preflightRow =
    state.kind === "blocked" || state.kind === "ready" ? state.row : null;

  return (
    <div data-screen-label="Artwork generator" className="pp-artwork">
      <Surface>
        <div className="pp-artwork__identity">
          <div className="pp-artwork__name">{name.text}</div>
          <div className="pp-artwork__gtin">{row.gtin14}</div>
          <p className="pp-artwork__lede">
            Everything here is decided on the server before a file exists. What a code
            points at goes on the label, and a label cannot be edited — so the checks sit
            in front of the press rather than after it.
          </p>
        </div>
      </Surface>

      <ArtworkSettingsForm
        settings={settings}
        lots={(batches.data?.batches ?? []).map((b) => b.lot)}
        fix={preflightRow === null ? null : xDimensionFix(preflightRow)}
        onChange={setSettings}
      />

      {state.kind === "unasked" ? (
        <Surface>
          <div data-screen-label="Awaiting a module width">
            <div className="pp-artwork__title">Tell us the module width first</div>
            <p>
              The module width is the size of one square in the symbol, in millimetres,
              and it is the number your printer will ask you for. Nothing is checked and
              nothing is generated until it is set, because a symbol whose physical size
              we inferred is a symbol that scans at the wrong size.
            </p>
          </div>
        </Surface>
      ) : null}

      {state.kind === "loading" ? (
        <Surface>
          <p data-screen-label="Running preflight">
            Checking this code against every gate&hellip;
          </p>
        </Surface>
      ) : null}

      {state.kind === "unavailable" ? (
        <Surface tone="error" emphasis="filled">
          <p data-screen-label="Preflight unavailable">{state.detail}</p>
        </Surface>
      ) : null}

      {state.kind === "blocked" ? (
        <GateRefusalCard
          row={state.row}
          publishing={publish.isPending}
          onPublish={() => publish.mutate()}
          onEscape={() => setConfirming("escape")}
        />
      ) : null}

      {state.kind === "ready" ? (
        <ReadyToPrint
          row={state.row}
          settings={settings}
          previewUrl={previewUrl}
          previewWidthMm={previewFile?.geometry?.widthMm ?? null}
          pending={download.isPending}
          onDownload={() =>
            requiresPrintConfirmation(state.row)
              ? setConfirming("print")
              : download.mutate(false)
          }
        />
      ) : null}

      {publish.error === null ? null : (
        <Surface tone="error" emphasis="filled">
          {/*
            The publish gate's own body, rendered from the wire like every other refusal on
            this screen. `PREFIX_NOT_VERIFIED` reaching a person here is §12(2) working.
          */}
          <p data-screen-label="Publish refused">
            {problemOf(publish.error)?.detail ?? "That product could not be published."}
          </p>
        </Surface>
      )}

      {refusal === null ? null : (
        <Surface tone="error" emphasis="filled">
          <p data-screen-label="Generation refused">{refusal}</p>
        </Surface>
      )}

      {receipt === null ? null : <DownloadReceipt confirmation={receipt} />}

      {confirming === "print" && preflightRow !== null ? (
        <PrintAnywayDialog
          row={preflightRow}
          running={download.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => download.mutate(false)}
        />
      ) : null}

      {confirming === "escape" && preflightRow !== null ? (
        <EscapeHatchDialog
          row={preflightRow}
          running={download.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => download.mutate(true)}
        />
      ) : null}

      {org.data === undefined ? null : <BatchZipGate plan={org.data.plan} zip={zip} />}

      <p>
        <Link to="/products/$productId" params={{ productId }}>
          Back to this product
        </Link>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The controls
// ---------------------------------------------------------------------------

/**
 * The settings, and the one field with no default.
 *
 * `fix` is the server's `targetXDimensionMm` arriving as a button that carries the number —
 * `components.md`'s rule for every one-click fix in the product. It renders **only** when a
 * refusal supplied one, which is what keeps this bundle from holding a GS1 constant of its
 * own; `artwork.ts`'s `EMPTY_SETTINGS` argues that at length.
 */
export function ArtworkSettingsForm({
  settings,
  lots,
  fix,
  onChange,
}: {
  settings: ArtworkSettings;
  lots: readonly string[];
  fix: { label: string; value: number } | null;
  onChange: (next: ArtworkSettings) => void;
}): ReactNode {
  const ecc = eccChoices(settings.symbology);
  return (
    <Surface>
      <div className="pp-artwork__settings" data-screen-label="Artwork settings">
        <div className="pp-artwork__title">What gets made</div>

        <fieldset className="pp-artwork__field">
          <legend>Symbol</legend>
          {SYMBOLOGY_CHOICES.map((value) => (
            <label key={value} className="pp-artwork__choice">
              <input
                type="radio"
                name="symbology"
                value={value}
                checked={settings.symbology === value}
                onChange={() => onChange({ ...settings, symbology: value })}
              />
              <span>{SYMBOLOGY_LABELS[value]}</span>
            </label>
          ))}
        </fieldset>

        <fieldset className="pp-artwork__field">
          <legend>File</legend>
          {FORMAT_CHOICES.map((value) => (
            <label key={value} className="pp-artwork__choice">
              <input
                type="radio"
                name="format"
                value={value}
                checked={settings.format === value}
                onChange={() => onChange({ ...settings, format: value })}
              />
              <span>{FORMAT_LABELS[value]}</span>
            </label>
          ))}
          {/*
            §8's *"generated alongside"*, on this screen's existing grain — a CHECKBOX beside
            the four radios rather than a fifth radio option. The sheet is a document about
            the symbol, so it is orthogonal to which file the symbol is drawn into, and a
            fifth option would have made it something you get *instead of* artwork.

            **Offered here even when the three vectors above are refused**, which is the one
            line on this screen a reader should suspect and the one `artwork.ts`'s
            PLACEMENT_SHEET_LABEL answers in full: the sheet carries no symbol, so §8.1 row 2
            has nothing to withhold, and §8.2 singles it out as the artefact gating would make
            the product *less* correct.
          */}
          <label className="pp-artwork__choice">
            <input
              type="checkbox"
              name="placementSheet"
              checked={settings.placementSheet}
              onChange={(e) =>
                onChange({ ...settings, placementSheet: e.target.checked })
              }
            />
            <span>{PLACEMENT_SHEET_LABEL}</span>
          </label>
        </fieldset>

        <Input
          label="Module width (mm)"
          value={settings.xDimensionMm}
          mono
          inputMode="decimal"
          autoComplete="off"
          placeholder="e.g. 0.495"
          onChange={(e) => onChange({ ...settings, xDimensionMm: e.target.value })}
        />
        <p className="pp-artwork__note">
          The width of one module — the smallest square in the symbol. Your printer will
          ask for this number, and the minimum depends on where the pack is scanned.
        </p>
        {fix === null ? null : (
          <Button
            variant="secondary"
            onClick={() => onChange({ ...settings, xDimensionMm: String(fix.value) })}
          >
            {fix.label}
          </Button>
        )}

        {ecc.length === 0 ? (
          <p className="pp-artwork__note">
            Data Matrix has no error-correction setting. ECC200 is fixed by the standard,
            so there is nothing here to choose.
          </p>
        ) : (
          <fieldset className="pp-artwork__field">
            <legend>Error correction</legend>
            {ecc.map((value) => (
              <label key={value} className="pp-artwork__choice">
                <input
                  type="radio"
                  name="ecc"
                  value={value}
                  checked={settings.eccLevel === value}
                  onChange={() => onChange({ ...settings, eccLevel: value })}
                />
                <span>{value}</span>
              </label>
            ))}
          </fieldset>
        )}

        {/*
          §6's *batch-qualified code*. Only rendered where batches exist: a chooser with one
          option that says "all units" is a control whose only outcome is the state you are
          already in, which is the same objection that keeps the application-preset chooser
          off this screen.
        */}
        {lots.length === 0 ? null : (
          <fieldset className="pp-artwork__field">
            <legend>Which units</legend>
            <label className="pp-artwork__choice">
              <input
                type="radio"
                name="lot"
                checked={settings.lot === null}
                onChange={() => onChange({ ...settings, lot: null })}
              />
              <span>This product, all units</span>
            </label>
            {lots.map((lot) => (
              <label key={lot} className="pp-artwork__choice">
                <input
                  type="radio"
                  name="lot"
                  value={lot}
                  checked={settings.lot === lot}
                  onChange={() => onChange({ ...settings, lot })}
                />
                <span>Batch {lot}</span>
              </label>
            ))}
          </fieldset>
        )}
      </div>
    </Surface>
  );
}

// ---------------------------------------------------------------------------
// Blocked
// ---------------------------------------------------------------------------

/**
 * One refusal, rendered from the body and shaped by the act.
 *
 * `detail` first — it is RFC 9457's "why this happened" and it is what the person needs
 * before any button means anything — then `remedy.label`, which is what to do. The two are
 * never swapped: `gate.ts`'s own docblock calls that *"U17's composing-its-own-sentence
 * failure arriving from the opposite direction."*
 *
 * The `error` tone is the outer surface, so nothing nested inside may be toned —
 * `Surface`'s nesting refusal throws in development if it is, which is why the timeline and
 * the hatch below sit on plain markup rather than on their own coloured cards.
 */
export function GateRefusalCard({
  row,
  publishing,
  onPublish,
  onEscape,
}: {
  row: ArtworkPreflightRow;
  publishing: boolean;
  onPublish: () => void;
  onEscape: () => void;
}): ReactNode {
  const refused = row.refused;
  if (refused === undefined) return null;
  const shape = remedyShapeOf(row);
  const remedy = refused.remedy;

  return (
    <Surface tone="error" emphasis="filled">
      <div data-screen-label="Artwork blocked" data-gate-code={refused.code}>
        <strong className="pp-artwork__title">{refused.title}</strong>
        {/* The body's own sentence. Never one composed here. */}
        <p className="pp-artwork__detail">{refused.detail}</p>

        {/*
          The label appears **once**. Where the act is a button, the label *is* the button —
          `components.md`'s rule that a fix carries its own explanation — and printing it
          above as well would be the same authored sentence twice, which reads as a screen
          repeating itself rather than as one instruction.
        */}
        {remedy === undefined || shape === "inline-action" ? null : (
          // A `link` carries the action on the link itself, so one element per row holds it.
          <p
            className="pp-artwork__remedy"
            data-remedy-action={shape === "link" ? undefined : remedy.action}
          >
            {remedy.label}
          </p>
        )}

        {shape === "inline-action" && remedy !== undefined ? (
          <Button
            variant="primary"
            loading={publishing}
            onClick={onPublish}
            data-remedy-action={remedy.action}
          >
            {publishing ? "Publishing…" : remedy.label}
          </Button>
        ) : null}

        {shape === "link" && remedy?.action === "verify_prefix" ? (
          <Link
            to={PREFIX_SCREEN_PATH}
            className="pp-button"
            data-variant="primary"
            data-remedy-action={remedy.action}
          >
            Go to Settings → Organisation
          </Link>
        ) : null}

        {shape === "domain-wait" && remedy?.action === "wait_for_domain" ? (
          <DomainBlocked hostname={remedy.hostname} onEscape={onEscape} />
        ) : null}
      </div>
    </Surface>
  );
}

/**
 * §6's longest row: *"inline DNS timeline, auto-recheck with visible activity, … escape
 * hatch **below** those two"*.
 *
 * The order is asserted by construction rather than by a stylesheet: `BLOCKED_DOMAIN_EXITS`
 * is `["waiting", "escape"]` and the two blocks below are rendered in that order, with
 * nothing after the hatch. The mock's rationale is the reason — *"waiting is the better
 * outcome for nearly everyone, so it gets the primary button and the top position."*
 *
 * **The notify-me is absent and says so.** §6 asks for *"email me the moment it's live"*;
 * there is no mail transport in this repository, measured rather than assumed, so the form
 * would be a remedy that does not exist — c29's rule, which holds that is worse than
 * silence. What replaces it is the auto-recheck, which is real, plus a sentence saying the
 * page does the waiting so the person does not have to.
 */
export function DomainBlocked({
  hostname,
  onEscape,
}: {
  hostname: string;
  onEscape: () => void;
}): ReactNode {
  return (
    <div className="pp-artwork__domain" data-screen-label="Domain never served">
      <Timeline steps={dnsTimelineSteps(hostname)} />

      <div className="pp-artwork__exit" data-exit={BLOCKED_DOMAIN_EXITS[0]}>
        <p aria-live="polite">
          We are re-checking {hostname} every {String(RECHECK_INTERVAL_MS / 1000)} seconds
          on this screen, and this download unlocks by itself the moment it answers.
          Leaving the page open is the whole mechanism — there is nothing to press.
        </p>
        <p className="pp-artwork__note">
          We cannot email you when it goes live yet. Saying we would, and then not, is
          worse than saying so here.
        </p>
      </div>

      {/*
        Second, and last. Nothing renders after this block: an exit placed below the escape
        hatch would be an exit nobody in a hurry reads.
      */}
      <div className="pp-artwork__exit" data-exit={BLOCKED_DOMAIN_EXITS[1]}>
        <p>
          If you cannot wait, you can print against a packagepage address today. There is
          a permanent trade-off and we will explain it before anything is generated.
        </p>
        <Button variant="secondary" onClick={onEscape}>
          Use a packagepage address
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ready
// ---------------------------------------------------------------------------

/**
 * The passing state — the canvas, the warnings, the marks, and the download.
 *
 * §8.1's warn-only set renders **as warnings and never as blocks**, from the wire's own
 * `title` and `detail`. `requiresPrintConfirmation` decides whether the download goes
 * through the typed confirmation, and it decides it from the warning codes rather than from
 * anything this component knows.
 */
export function ReadyToPrint({
  row,
  settings,
  previewUrl,
  previewWidthMm,
  pending,
  onDownload,
}: {
  row: ArtworkPreflightRow;
  settings: ArtworkSettings;
  previewUrl?: string | undefined;
  previewWidthMm?: number | null;
  pending: boolean;
  onDownload: () => void;
}): ReactNode {
  const uri = encodedUri(row, settings.lot);
  return (
    <Surface>
      <div data-screen-label="Ready to print">
        <div className="pp-artwork__title">This code is clear to print</div>

        <ArtworkPreview
          uri={uri}
          watermarks={row.watermarks}
          {...(previewUrl === undefined ? {} : { pngUrl: previewUrl })}
          {...(previewWidthMm === null || previewWidthMm === undefined
            ? {}
            : { widthMm: previewWidthMm })}
        />

        {row.warnings.map((w) => (
          <Surface key={w.code} tone="warn" emphasis="filled">
            <div data-warning-code={w.code}>
              <strong>{w.title}</strong>
              <p>{w.detail}</p>
            </div>
          </Surface>
        ))}

        <WatermarkNotices marks={row.watermarks} />

        {row.disclosure === undefined ? null : (
          <Surface tone="neutral" emphasis="filled">
            <div data-screen-label="Platform address disclosure">
              <p>{row.disclosure.reassurance}</p>
              <p>
                <strong>{row.disclosure.permanence}</strong>
              </p>
              <p>{row.disclosure.alternative}</p>
            </div>
          </Surface>
        )}

        <Button variant="primary" loading={pending} onClick={onDownload}>
          {pending ? "Generating…" : "Download print files"}
        </Button>
      </div>
    </Surface>
  );
}

/**
 * The true-scale preview canvas — and **"true scale" is a claim a browser cannot make.**
 *
 * Read this before reaching for `devicePixelRatio`. It is a **pixel-density ratio**, not a
 * measurement of anything physical: it says how many device pixels a CSS pixel occupies, and
 * says nothing at all about how large that pixel is on the panel. CSS `mm` is no better — it
 * is defined against a nominal 96 dpi rather than against the display in front of the
 * person, so `21.8mm` is a fixed count of CSS pixels wearing a millimetre's name. There is
 * no API in a browser that returns the physical size of a screen, and there is no arithmetic
 * over the two above that recovers it.
 *
 * That is exactly why `components.md` specifies *"Shown at 100% — hold a ruler to your
 * screen"* and calls it a legitimate device. **The honesty is the feature.** The canvas
 * renders at the stated nominal scale and asks the person to check it against a physical
 * ruler, because a person with a ruler can answer the question and the page cannot. A
 * screen that computed a "true-scale factor" would be stating a measurement it invented, on
 * the one control whose entire purpose is a physical dimension.
 *
 * The symbol itself is the **PNG the API produced**, not a client-side drawing.
 * `apps/app` may not import `packages/barcode`, so there is no encoder in this bundle to
 * guess with — the same reason the scan-preview simulator is backed by the real merge
 * endpoint. It is also the right file to show: §8.2 makes the PNG a preview at every tier
 * and never a print deliverable, so the thing on screen is watermarked by construction and
 * cannot be mistaken for the artwork.
 */
export function ArtworkPreview({
  uri,
  watermarks,
  pngUrl,
  widthMm,
}: {
  uri: string | null;
  watermarks: readonly ArtworkWatermark[];
  pngUrl?: string;
  widthMm?: number;
}): ReactNode {
  return (
    <figure className="pp-artwork__canvas" data-screen-label="True-scale preview">
      {pngUrl === undefined ? (
        <div className="pp-artwork__canvas-empty">
          The preview renders here once the symbol has been generated. It is the real
          file, watermarked, rather than a drawing of one.
        </div>
      ) : (
        <img
          className="pp-artwork__canvas-image"
          src={pngUrl}
          alt="Preview of the generated symbol, watermarked"
          style={widthMm === undefined ? undefined : { width: `${String(widthMm)}mm` }}
        />
      )}
      <figcaption className="pp-artwork__canvas-note">
        Shown at 100% — <strong>hold a ruler to your screen</strong>. A web page cannot
        measure your display, so this is drawn at the standard nominal scale and checked
        by you rather than claimed by us.
      </figcaption>
      {uri === null ? null : <code className="pp-artwork__uri">{uri}</code>}
      {watermarks.length === 0 ? null : (
        <p className="pp-artwork__canvas-mark">
          This preview carries {String(watermarks.length)} watermark
          {watermarks.length === 1 ? "" : "s"} — see below for why.
        </p>
      )}
    </figure>
  );
}

/** §6's two watermark states, from the wire's set rather than from a guess about format. */
export function WatermarkNotices({
  marks,
}: {
  marks: readonly ArtworkWatermark[];
}): ReactNode {
  const notices = watermarkNotices(marks);
  if (notices.length === 0) return null;
  return (
    <div data-screen-label="Watermarks">
      {notices.map((notice) => (
        <Surface key={notice.headline} tone="warn" emphasis="filled">
          <div>
            <strong>{notice.headline}</strong>
            <p>{notice.body}</p>
          </div>
        </Surface>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The two confirmations
// ---------------------------------------------------------------------------

/**
 * §6's *"warn, domain ever-live but currently failing — typed confirmation, not a block"*.
 *
 * The warning's own `title` and `detail` are the copy; the challenge is four letters, and
 * `artwork.ts`'s `PRINT_CHALLENGE` carries the argument for why it is not the GTIN. Amber,
 * no disabled download, **no escape hatch offered** — the mock is explicit that converting a
 * working custom domain to a platform address over a network blip is the worst outcome
 * available, so the hatch is absent here and present two states over.
 */
export function PrintAnywayDialog({
  row,
  running,
  onCancel,
  onConfirm,
}: {
  row: ArtworkPreflightRow;
  running: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): ReactNode {
  const [typed, setTyped] = useState("");
  const challenge = checkPrintChallenge(typed);
  const warning = row.warnings.find((w) => w.code === "DOMAIN_EVER_LIVE_UNHEALTHY");

  return (
    <div
      className="pp-artwork__dialog"
      role="dialog"
      aria-modal="true"
      aria-label="Print anyway"
      data-screen-label="Print anyway"
    >
      <Surface tone="warn" emphasis="filled">
        <div>
          <div className="pp-artwork__title">
            {warning?.title ?? "Your domain is not answering our checks right now"}
          </div>
          <p>{warning?.detail}</p>
          <p>
            The address is yours and correctly delegated, so codes printed with it resolve
            once the blip clears. We are not going to stop you printing over a failed
            probe — but if this is going to a printer today, say so.
          </p>

          <Input
            label={`Type ${PRINT_CHALLENGE} to confirm`}
            value={typed}
            mono
            onChange={(e) => setTyped(e.target.value)}
            {...(challenge.firstWrongIndex === null
              ? {}
              : {
                  validation: {
                    state: "invalid" as const,
                    rule: `The confirmation word is ${PRINT_CHALLENGE}.`,
                    cause:
                      "Four letters, upper case, nothing else — it is a seatbelt rather " +
                      "than a wall.",
                    markFrom: challenge.firstWrongIndex,
                    markTo: challenge.firstWrongIndex + 1,
                  },
                })}
          />

          <div className="pp-artwork__dialog-actions">
            <Button variant="secondary" onClick={onCancel}>
              Not now
            </Button>
            <Button
              variant="primary"
              disabled={!challenge.satisfied}
              loading={running}
              onClick={onConfirm}
            >
              {running ? "Generating…" : "Download anyway"}
            </Button>
          </div>
        </div>
      </Surface>
    </div>
  );
}

/**
 * §8.3's escape hatch, disclosed — *"at the moment, not buried"*.
 *
 * Every sentence comes from `remedy.escapeHatch.disclosure`, which `artwork-copy.ts` authors
 * once for this screen and for Phase 4's domain-setup explainer, so the two say the
 * permanence sentence **byte for byte**. Nothing here paraphrases it.
 *
 * The ordering is the mock's ruling and it is a claim about which door is recommended:
 * *"'Wait for my domain' is styled as the recommended action and the proceed button is quiet
 * and disabled until the checkbox is ticked."* Both doors are open; neither is hidden.
 */
export function EscapeHatchDialog({
  row,
  running,
  onCancel,
  onConfirm,
}: {
  row: ArtworkPreflightRow;
  running: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): ReactNode {
  const [acknowledged, setAcknowledged] = useState(false);
  const remedy = row.refused?.remedy;
  if (remedy?.action !== "wait_for_domain") return null;
  const { disclosure, digitalLinkUri, platformHost } = remedy.escapeHatch;

  return (
    <div
      className="pp-artwork__dialog"
      role="dialog"
      aria-modal="true"
      aria-label="Print with a packagepage address"
      data-screen-label="Escape hatch"
    >
      <Surface tone="neutral" emphasis="filled">
        <div>
          <div className="pp-artwork__title">
            These codes will answer forever — and they will never be yours to move
          </div>

          <p>{disclosure.reassurance}</p>
          <p className="pp-artwork__permanence">
            <strong>{disclosure.permanence}</strong>
          </p>
          <p>{disclosure.alternative}</p>

          <p className="pp-artwork__note">
            The symbol would encode <code>{digitalLinkUri}</code>, on{" "}
            <code>{platformHost}</code>.
          </p>

          <label className="pp-artwork__choice">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            <span>
              I understand these codes can never move to my own domain without reprinting.
            </span>
          </label>

          <div className="pp-artwork__dialog-actions">
            {/* The recommended door, first and loudest. */}
            <Button variant="primary" onClick={onCancel}>
              Wait for my domain
            </Button>
            <Button
              variant="quiet"
              disabled={!acknowledged}
              loading={running}
              onClick={onConfirm}
            >
              {running ? "Generating…" : "Print with a packagepage address"}
            </Button>
          </div>
        </div>
      </Surface>
    </div>
  );
}

// ---------------------------------------------------------------------------
// After
// ---------------------------------------------------------------------------

/**
 * §6's *"download confirmation stating format, physical size, symbology and GTIN"*.
 *
 * Four facts, and the mock's reason for restating all of them: *"A user who downloads the
 * wrong product's artwork finds out here, not at the print shop."* The size is omitted
 * rather than guessed when the response carried no geometry — see `ArtworkPreview` on why an
 * invented measurement is the wrong failure on this screen.
 */
export function DownloadReceipt({
  confirmation,
}: {
  confirmation: DownloadConfirmation;
}): ReactNode {
  return (
    <Surface tone="live" emphasis="filled">
      <div data-screen-label="Downloaded">
        <div className="pp-artwork__title">Downloaded</div>
        <dl className="pp-artwork__receipt">
          <dt>Code</dt>
          <dd className="pp-artwork__gtin">{confirmation.gtin14}</dd>
          <dt>Symbology</dt>
          <dd>{confirmation.symbology}</dd>
          <dt>Printed size</dt>
          <dd>{confirmation.size ?? "Not reported by this build"}</dd>
          <dt>Format</dt>
          <dd>{confirmation.format}</dd>
          <dt>File</dt>
          <dd className="pp-artwork__gtin">{confirmation.filename}</dd>
          {confirmation.sheetFilename === null ? null : (
            <>
              <dt>Placement sheet</dt>
              <dd className="pp-artwork__gtin">{confirmation.sheetFilename}</dd>
            </>
          )}
        </dl>
        {confirmation.marks.map((mark) => (
          <p key={mark.headline}>{mark.headline}</p>
        ))}
      </div>
    </Surface>
  );
}

/**
 * §6's *"batch/catalog ZIP gate (Starter)"* — the locked gate below Starter, the control at
 * Starter and above. Never hidden in either arm.
 *
 * Until U62 the Starter arm rendered `null`: an honest absence over a job nobody had built
 * (U57). The job exists now, so the absence becomes the control.
 *
 * No price is quoted in either arm. `plans.ts` holds every number that appears beside one
 * and this screen has no reason to be a second place they are printed.
 */
export function BatchZipGate({
  plan,
  zip,
}: {
  readonly plan: Tier;
  readonly zip: BatchZipView;
}): ReactNode {
  if (planRank(plan) >= planRank(BATCH_ZIP_TIER)) {
    return <BatchZipControl {...zip} />;
  }
  return (
    <div data-screen-label="Batch ZIP gate">
      <PlanGate
        plan={BATCH_ZIP_TIER}
        feature="Batch and catalogue artwork, as one ZIP"
        why={
          "Generating every batch or a whole catalogue at once is work we do on our " +
          "machines rather than on yours, and it is the point where a catalogue outgrows " +
          "one-symbol-at-a-time."
        }
        instead={
          "Single symbols stay free at every tier, in every format, with the full preflight " +
          "— including this one. Nothing about the barcode is better on a paid plan."
        }
      />
    </div>
  );
}

/**
 * Everything the Starter arm renders, as data — so a static render can reach both arms.
 *
 * The queries, the mutation and the batch id live in {@link ArtworkGenerator}. This file's
 * own header states the rule and the two shipped defects behind it: `apps/app`'s suite is
 * `renderToStaticMarkup`, which runs no effects, so a component that fetched for itself
 * would be a state the suite cannot reach.
 */
export interface BatchZipView {
  /** How many published products the job would render. */
  readonly productCount: number;
  /** Whether the settings carry a module width. Without one there is nothing to ask. */
  readonly sized: boolean;
  readonly starting: boolean;
  /** The server's sentence, never one this screen composed. */
  readonly refused: string | null;
  readonly batch: ArtworkBatchStatusView | null;
  readonly onStart: () => void;
}

/**
 * The Starter arm: generate every published product's symbol as one ZIP, and watch it.
 *
 * **Every published product, not the one on screen.** This screen is per-product and the
 * job is not — §8.2's line is about a catalogue outgrowing one-symbol-at-a-time, and a ZIP
 * of one file would answer nothing.
 *
 * The button says which of the two reasons it is disabled for, because *"choose a module
 * width"* and *"nothing published yet"* ask the person for different acts.
 */
export function BatchZipControl({
  productCount,
  sized,
  starting,
  refused,
  batch,
  onStart,
}: BatchZipView): ReactNode {
  return (
    <Surface tone="neutral" emphasis="filled">
      <div data-screen-label="Batch ZIP">
        <div className="pp-artwork__title">Every published product, as one ZIP</div>
        <Button onClick={onStart} disabled={!sized || productCount === 0 || starting}>
          {!sized
            ? "Choose a module width first"
            : productCount === 0
              ? "Nothing published yet"
              : `Generate ${String(productCount)} symbol(s)`}
        </Button>

        {refused === null ? null : <p data-screen-label="Batch ZIP refused">{refused}</p>}

        {batch === null ? null : <BatchZipProgress batch={batch} />}
      </div>
    </Surface>
  );
}

/**
 * What the job has done so far, stated rather than animated.
 *
 * The withheld count is rendered whenever it is above zero and names `report.csv` by file
 * name, because §8.1's *"never failing whole"* is only useful to somebody who knows where
 * the list of withheld rows went.
 */
export function BatchZipProgress({
  batch,
}: {
  readonly batch: ArtworkBatchStatusView;
}): ReactNode {
  return (
    <div data-screen-label={`Batch ZIP ${batch.status}`}>
      <dl className="pp-artwork__receipt">
        <dt>Status</dt>
        <dd>{BATCH_STATUS_LABELS[batch.status]}</dd>
        <dt>Symbols</dt>
        <dd>{String(batch.entries)}</dd>
        {batch.withheld === 0 ? null : (
          <>
            <dt>Withheld</dt>
            <dd>
              {String(batch.withheld)} — listed in {ARTWORK_BATCH_REPORT_NAME} inside the
              ZIP, with the reason for each
            </dd>
          </>
        )}
      </dl>
      {batch.error === null ? null : <p>{batch.error}</p>}
      {batch.downloadUrl === null ? null : (
        <p>
          <a href={batch.downloadUrl} download={batchZipName(batch.id)}>
            Download the ZIP
          </a>
        </p>
      )}
      {batch.status === "ready" && batch.expired ? (
        <p>
          This ZIP has expired. Generate it again — the files are the same, and nothing
          about the symbols has changed.
        </p>
      ) : null}
    </div>
  );
}

/**
 * The batch ZIP's network half — the queries, the mutation, and the id the poll follows.
 *
 * Exported so its absence from the static suite is a stated fact rather than an oversight:
 * it is an effect, and no `renderToStaticMarkup` will ever run it. What it produces is
 * {@link BatchZipView}, which the suite renders directly.
 *
 * **The poll runs only while the job is queued or running**, which is the rule the
 * preflight's own `refetchInterval` follows. A screen that polled a finished job would be
 * a timer on a row that cannot move again.
 */
export function useBatchZip(settings: ArtworkSettings): BatchZipView {
  const [batchId, setBatchId] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);

  const published = useQuery({
    queryKey: ["products", "list", "published"],
    queryFn: () => api.products({ status: "published" }),
  });

  const status = useQuery({
    queryKey: ["artwork", "batch", batchId],
    queryFn: () => readBatchZip(batchId as string),
    enabled: batchId !== null,
    refetchInterval: (query) => {
      const outcome = query.state.data;
      if (outcome === undefined || !outcome.ok) return false;
      return outcome.batch.status === "queued" || outcome.batch.status === "running"
        ? BATCH_POLL_INTERVAL_MS
        : false;
    },
  });

  const xDimensionMm = xDimensionOf(settings);
  const productIds = (published.data?.products ?? []).map((row) => row.id);

  const start = useMutation({
    mutationFn: () =>
      startBatchZip({
        productIds,
        symbology: settings.symbology,
        format: settings.format,
        xDimensionMm: xDimensionMm as number,
        applicationPreset: "retail-pos",
        eccLevel: settings.eccLevel,
        zip: true,
      }),
    onSuccess: (outcome) => {
      if (outcome.ok) {
        setBatchId(outcome.batch.id);
        setRefused(null);
        return;
      }
      // The sentence is the server's, never this screen's — U17's rule, the same one every
      // refusal above follows. A `PLAN_GATED` body arriving here means the organisation
      // dropped below Starter between the render and the click.
      setRefused(outcome.problem?.detail ?? "That batch could not be started.");
    },
  });

  return {
    productCount: productIds.length,
    sized: xDimensionMm !== null,
    starting: start.isPending,
    refused,
    batch: status.data?.ok === true ? status.data.batch : null,
    onStart: () => start.mutate(),
  };
}

/** One label per stored status. A screen never prints a wire constant at a person. */
const BATCH_STATUS_LABELS: Readonly<Record<ArtworkBatchStatus, string>> = {
  queued: "Queued",
  running: "Generating",
  ready: "Ready",
  failed: "Failed",
};

/**
 * How often the screen asks where the job has got to, while it is still moving.
 *
 * Three seconds: a 500-symbol render is tens of seconds of work, so this is frequent
 * enough that the state looks live and rare enough that a forgotten tab costs one request
 * every three seconds against a row read.
 */
const BATCH_POLL_INTERVAL_MS = 3_000;

/**
 * The preview fetch, kept out of the render path.
 *
 * Exported so the screen can adopt it and so its absence from the static suite is a stated
 * fact rather than an oversight: it is an effect, it is in `C93_FLOWS`, and no
 * `renderToStaticMarkup` will ever run it.
 */
export function usePreviewObjectUrl(blob: Blob | null): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (blob === null) {
      setUrl(undefined);
      return;
    }
    const next = URL.createObjectURL(blob);
    setUrl(next);
    // Revoked on the way out rather than on the way in: a URL revoked while the `img` that
    // holds it is still mounted is a broken image, and the previous render's element is
    // still on screen until this effect's cleanup has run.
    return () => {
      URL.revokeObjectURL(next);
    };
  }, [blob]);
  return url;
}
